/**
 * Stable error codes of `requestOrgCreation` (app/start/actions.ts, ALU-515).
 * The action answers with a code (plus an English fallback); each form turns
 * the code into its own language. The Encuentro form reads
 * `encuentro.schoolSignup.start.errors.<code>` in messages/encuentro/*.
 * `lib/schemas/start-org.ts` raises the validation ones as the issue message.
 */
export const START_ERROR_CODES = [
  'name_invalid',
  'full_name_invalid',
  'slug_invalid',
  'slug_reserved',
  'slug_taken',
  'email_invalid',
  'attestation_required',
  'rate_limited',
  'pending_exists',
  'save_failed',
  'magic_link_failed',
  'unknown',
] as const

export type StartErrorCode = (typeof START_ERROR_CODES)[number]

/** Fallback for a form with no translations of its own (the classic /start). */
export const START_ERROR_ENGLISH: Record<StartErrorCode, string> = {
  name_invalid: 'Organization name must be between 2 and 80 characters',
  full_name_invalid: 'Please enter your name (2 to 80 characters)',
  slug_invalid: 'That URL is invalid',
  slug_reserved: 'That URL is reserved — please choose another',
  slug_taken: 'That URL is already taken — please choose another',
  email_invalid: 'Please enter a valid email',
  attestation_required: 'Please confirm you are authorized to create this organization',
  rate_limited: 'Too many attempts. Please try again later.',
  pending_exists:
    'There is already a pending request for this email. Use its confirmation email or try again in an hour.',
  save_failed: 'Could not save your request. Please try again.',
  magic_link_failed: 'Could not send the confirmation email. Please try again.',
  unknown: 'Something went wrong. Please try again.',
}

export function isStartErrorCode(value: unknown): value is StartErrorCode {
  return typeof value === 'string' && (START_ERROR_CODES as readonly string[]).includes(value)
}
